import {
  Box,
  Button,
  Checkmark,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Listbox,
  Popover,
  Spinner,
  Text,
  createListCollection,
  useListboxContext,
  useListboxItemContext,
} from "@chakra-ui/react-v3";
import { ChangeEvent, RefObject, useState } from "react";
import { FiFilter, FiSearch, FiX } from "react-icons/fi";
import { useCustomKeywords } from "../medical/patient-overview-v2-content/hooks/use-custom-keywords";
import { useSharedContext } from "../medical/shared-logic/shared-context";
import { MEDICAL_CATEGORIES, type MedicalCategory } from "./medical-keywords";

type MedicalKeywordSearchProps = {
  filterInputRef: RefObject<HTMLInputElement>;
  filterQuery: string;
  handleFilterChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleClear: () => void;
  setFilter: (value: string) => void;
  isLoadingData: boolean;
  isIndexing: boolean;
};

/**
 * Medical keyword search component that combines a text search input
 * with a medical keyword selector using Chakra UI Listbox.
 */
export function MedicalKeywordSearch({
  filterInputRef,
  handleFilterChange,
  handleClear,
  setFilter,
  isLoadingData,
  isIndexing,
}: MedicalKeywordSearchProps) {
  const {
    sharedState: { isAmazonUser },
  } = useSharedContext();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [activeKeywordCount, setActiveKeywordCount] = useState(0);
  const hasActiveKeywords = activeKeywordCount > 0;

  // When user types in search, clear predefined filters (switch to manual mode)
  const handleManualSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim().length > 0 && hasActiveKeywords) {
      setActiveKeywordCount(0);
      setFilter("");
    }
    handleFilterChange(e);
  };

  // When user selects keywords, switch to predefined mode, clear manual search input
  const handleKeywordAdd = (keywords: string[], itemCount: number) => {
    if (filterInputRef.current) {
      filterInputRef.current.value = "";
    }
    const predefinedQuery = keywords.map(kw => (kw.includes(" ") ? `"${kw}"` : kw)).join(" ");
    setActiveKeywordCount(itemCount);
    setFilter(predefinedQuery);
  };

  const handleClearAll = () => {
    handleClear();
    setActiveKeywordCount(0);
  };

  // Check if manual search is active
  const hasManualSearch = (filterInputRef.current?.value?.trim().length ?? 0) > 0;

  return (
    <HStack justify="center" gap={2} width="full">
      <HStack gap={2} flex="0 1 auto" maxW="500px" width="full">
        <InputGroup
          display="flex"
          flex="1"
          startElement={<FiSearch />}
          endElement={
            <>
              {(hasManualSearch || hasActiveKeywords) && (
                <Box position="absolute" right="4" top="50%" transform="translateY(-50%)">
                  <IconButton
                    size="xs"
                    aria-label="Clear search"
                    onClick={handleClearAll}
                    variant="ghost"
                    color="fg.muted"
                    borderRadius="full"
                    bg="interactive.hover"
                    _hover={{
                      bg: "interactive.active",
                      color: "fg",
                    }}
                  >
                    <FiX />
                  </IconButton>
                </Box>
              )}
            </>
          }
        >
          <Input
            ref={filterInputRef}
            bg="bg.surface"
            placeholder={
              hasActiveKeywords
                ? `${activeKeywordCount} filter${activeKeywordCount !== 1 ? "s" : ""} selected`
                : "Filter all tables..."
            }
            my={2}
            onChange={handleManualSearchChange}
            transition="all 0.15s ease-in-out"
            boxShadow="0 0 0 1px transparent"
            borderColor="border.emphasized"
            _hover={{ borderColor: "focusRing" }}
            _focus={{
              borderColor: "focusRing",
              boxShadow: "0 0 0 3px token(colors.focusRing.shadow)",
              outline: "none",
            }}
            disabled={isLoadingData || isIndexing || hasActiveKeywords}
          />
        </InputGroup>

        {isAmazonUser && (
          <Popover.Root
            open={isPopoverOpen}
            onInteractOutside={() => setIsPopoverOpen(false)}
            positioning={{ placement: "bottom-end" }}
          >
            <Popover.Trigger asChild>
              <Box position="relative">
                <IconButton
                  aria-label="Medical keyword filter"
                  size="md"
                  p={1}
                  disabled={isLoadingData || isIndexing || hasManualSearch}
                  bg="bg.surface"
                  color="fg.emphasized"
                  borderWidth="1px"
                  borderColor="border.emphasized"
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                  _hover={{ borderColor: "focusRing", bg: "bg.canvas" }}
                >
                  <FiFilter />
                </IconButton>
                {hasActiveKeywords && (
                  <Box
                    position="absolute"
                    top="-1"
                    right="-1"
                    bg="blue.500"
                    color="white"
                    borderRadius="full"
                    minW="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="xs"
                    fontWeight="bold"
                    px={1}
                  >
                    {activeKeywordCount}
                  </Box>
                )}
              </Box>
            </Popover.Trigger>
            <Popover.Positioner zIndex={1500}>
              <Popover.Content
                width="400px"
                maxH="500px"
                bg="bg.surface"
                borderColor="border"
                borderWidth="1px"
                borderRadius="md"
                boxShadow="lg"
                p={0}
              >
                <KeywordSelector
                  onApply={(keywords, itemCount) => {
                    handleKeywordAdd(keywords, itemCount);
                    setIsPopoverOpen(false);
                  }}
                  onCancel={() => setIsPopoverOpen(false)}
                />
              </Popover.Content>
            </Popover.Positioner>
          </Popover.Root>
        )}
      </HStack>

      {isIndexing && (
        <HStack gap={1} color="fg.muted" opacity={0.4} fontSize="sm" flexShrink={0}>
          <Spinner size="sm" />
          <Text>Indexing</Text>
        </HStack>
      )}
    </HStack>
  );
}

type KeywordSelectorProps = {
  onApply: (keywords: string[], itemCount: number) => void;
  onCancel: () => void;
};

/**
 * Keyword selector using Chakra Listbox with grouped categories.
 * Supports parent-child selection and "Select All" functionality.
 */
function KeywordSelector({ onApply, onCancel }: KeywordSelectorProps) {
  const { customKeywords } = useCustomKeywords();
  const allItems: ListboxItem[] = [];
  const keywordSearchTermsMap = new Map<string, string[]>();

  // Build categories array with custom keywords at the start if they exist
  const categoriesToRender: MedicalCategory[] = [];

  if (customKeywords.length > 0) {
    const customCategory: MedicalCategory = {
      label: "Your Custom Keywords",
      value: "custom",
      keywords: customKeywords.map(keyword => ({
        label: keyword,
        value: keyword,
      })),
    };
    categoriesToRender.push(customCategory);
  }

  categoriesToRender.push(...MEDICAL_CATEGORIES);

  categoriesToRender.forEach(category => {
    // Add category as parent item
    allItems.push({
      label: category.label,
      value: category.value,
      isCategory: true,
      categoryValue: category.value,
    });

    // Add keywords as child items
    category.keywords.forEach((keyword, idx) => {
      // Create unique value identifier for listbox
      const uniqueValue = `${category.value}-keyword-${idx}`;

      // Normalize keyword.value to always be an array of search terms
      const searchTerms = Array.isArray(keyword.value) ? keyword.value : [keyword.value];

      keywordSearchTermsMap.set(uniqueValue, searchTerms);

      allItems.push({
        label: keyword.label,
        value: uniqueValue,
        isCategory: false,
        categoryValue: category.value,
        searchTerms,
      });
    });
  });

  const collection = createListCollection({ items: allItems });

  return (
    <Box>
      <Listbox.Root collection={collection} selectionMode="multiple" gap="0">
        <ListboxHeader collection={collection} />
        <Listbox.Content maxH="350px" roundedTop="0" overflowY="auto">
          {categoriesToRender.map(category => {
            // Get unique values for this category's keywords
            const categoryKeywordValues = category.keywords.map(
              (_, idx) => `${category.value}-keyword-${idx}`
            );

            return (
              <Listbox.ItemGroup key={category.value}>
                <CategoryItem category={category} categoryKeywordValues={categoryKeywordValues} />
                {category.keywords.map((keyword, idx) => {
                  const uniqueValue = `${category.value}-keyword-${idx}`;
                  return (
                    <Listbox.Item
                      item={{ label: keyword.label, value: uniqueValue }}
                      key={uniqueValue}
                      pl={8}
                    >
                      <ListboxItemCheckmark />
                      <Listbox.ItemText>{keyword.label}</Listbox.ItemText>
                    </Listbox.Item>
                  );
                })}
              </Listbox.ItemGroup>
            );
          })}
        </Listbox.Content>
        <ListboxFooter onApply={onApply} onCancel={onCancel} allItems={allItems} />
      </Listbox.Root>
    </Box>
  );
}

type ListboxItem = {
  label: string;
  value: string;
  isCategory?: boolean;
  categoryValue?: string;
  searchTerms?: string[];
};

/**
 * Header with "Select All" checkbox
 */
function ListboxHeader({ collection }: { collection: ReturnType<typeof createListCollection> }) {
  const listbox = useListboxContext();
  const isAllSelected = listbox.value.length === collection.items.length;
  const isSomeSelected = listbox.value.length > 0 && listbox.value.length < collection.items.length;

  function handleSelectAll() {
    if (isAllSelected) {
      listbox.setValue([]);
    } else {
      listbox.setValue(collection.items.map((item: ListboxItem) => item.value));
    }
  }

  return (
    <Flex
      as="button"
      onClick={handleSelectAll}
      px="3"
      gap="2"
      align="center"
      cursor="pointer"
      borderWidth="1px"
      minH="10"
      roundedTop="l2"
      mb="-1px"
      bg="bg.surface"
      _hover={{ bg: "bg.muted" }}
    >
      <Checkmark
        filled
        size="sm"
        checked={isAllSelected}
        indeterminate={isSomeSelected}
        colorPalette="blue"
      />
      <Listbox.Label>Select Medical Keywords</Listbox.Label>
    </Flex>
  );
}

/**
 * Category item that handles selecting/deselecting all children
 */
function CategoryItem({
  category,
  categoryKeywordValues,
}: {
  category: MedicalCategory;
  categoryKeywordValues: string[];
}) {
  const listbox = useListboxContext();
  const selectedValues = listbox.value as string[];

  // Check if all children are selected
  const allChildrenSelected = categoryKeywordValues.every(val => selectedValues.includes(val));
  // Check if some but not all children are selected
  const someChildrenSelected =
    !allChildrenSelected && categoryKeywordValues.some(val => selectedValues.includes(val));

  function handleCategoryClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (allChildrenSelected) {
      // Deselect all children
      const newValues = selectedValues.filter(val => !categoryKeywordValues.includes(val));
      listbox.setValue(newValues);
    } else {
      // Select all children (add any that aren't already selected)
      const newValues = [...new Set([...selectedValues, ...categoryKeywordValues])];
      listbox.setValue(newValues);
    }
  }

  return (
    <Flex
      as="button"
      onClick={handleCategoryClick}
      px="3"
      gap="2"
      align="center"
      cursor="pointer"
      minH="10"
      bg="bg.subtle"
      fontWeight="medium"
      _hover={{ bg: "bg.muted" }}
      width="full"
    >
      <Checkmark
        filled
        size="sm"
        checked={allChildrenSelected}
        indeterminate={someChildrenSelected}
        colorPalette="blue"
      />
      <Text fontSize="sm">{category.label}</Text>
    </Flex>
  );
}

/**
 * Checkmark for individual items
 */
function ListboxItemCheckmark() {
  const itemState = useListboxItemContext();
  return (
    <Checkmark
      filled
      size="sm"
      checked={itemState.selected}
      disabled={itemState.disabled}
      colorPalette="blue"
    />
  );
}

/**
 * Footer with Clear All, Cancel, and Apply buttons
 */
function ListboxFooter({
  onApply,
  onCancel,
  allItems,
}: {
  onApply: (keywords: string[], itemCount: number) => void;
  onCancel: () => void;
  allItems: ListboxItem[];
}) {
  const listbox = useListboxContext();

  function handleApply() {
    const selectedValues = listbox.value as string[];

    // Extract search terms from selected items
    const allTerms: string[] = [];
    selectedValues.forEach(value => {
      const item = allItems.find(i => i.value === value);
      if (item?.searchTerms) {
        allTerms.push(...item.searchTerms);
      }
    });

    // Calculate keyword count (excluding categories)
    const itemCount = selectedValues.filter(value => {
      const item = allItems.find(i => i.value === value);
      return item && !item.isCategory;
    }).length;

    onApply(allTerms, itemCount);
  }

  function handleClearAll() {
    listbox.setValue([]);
  }

  // Calculate keyword count (excluding categories)
  const selectedValues = listbox.value as string[];
  const keywordCount = selectedValues.filter(value => {
    const item = allItems.find(i => i.value === value);
    return item && !item.isCategory;
  }).length;

  return (
    <Flex
      gap={2}
      p={3}
      borderTopWidth="1px"
      borderColor="border.muted"
      bg="bg.surface"
      justifyContent="space-between"
    >
      <Button
        size="sm"
        variant="ghost"
        onClick={handleClearAll}
        disabled={keywordCount === 0}
        color="fg.muted"
      >
        Clear All
      </Button>
      <Flex gap={2}>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          bg="btn.info.bg"
          color="btn.info.color"
          _hover={{ borderColor: "btn.info.hoverBorder" }}
          onClick={handleApply}
        >
          Apply {keywordCount > 0 ? `(${keywordCount})` : ""}
        </Button>
      </Flex>
    </Flex>
  );
}
